<template>
  <div class="drug-target-container">
    <div class="module-layout">
      <!-- Left Control Panel -->
      <div class="parameter-panel">
        <h4><i class="el-icon-first-aid-kit"></i> Protein Selection</h4>
        <div class="parameter-container">
          <div class="parameter-group">
            <label>
              Select Proteins
              <el-tooltip content="Paste proteins (comma, space, or newline separated)" placement="top">
                <i class="el-icon-info" style="color: #94a3b8; cursor: help; font-size: 12px;"></i>
              </el-tooltip>
            </label>
            <el-select 
              v-model="selectedProteins" 
              multiple 
              filterable
              clearable
              remote
              :reserve-keyword="false"
              :remote-method="fetchGenes"
              :loading="loadingGenes"
              placeholder="Type to search proteins"
              style="width: 100%"
              @paste.native="handleGenePaste"
            >
              <el-option v-for="protein in gene_list" :key="protein" :label="protein" :value="protein" />
            </el-select>
          </div>
          
          <div class="parameter-group">
            <span class="selected-info">{{ selectedProteins.length }} proteins selected</span>
          </div>
          
          <div class="parameter-group">
            <label>Number of drugs displayed per gene</label>
            <el-slider 
              v-model="maxDrugsPerGene" 
              :min="5" 
              :max="100" 
              :step="5"
              show-input
              :show-input-controls="false"
              input-size="mini"
            />
          </div>
          
          <div class="parameter-footer">
            <div class="auto-update-hint">
              <span class="auto-pill">Auto update</span>
              <span v-if="loadingAnalysis" class="auto-status">Updating…</span>
              <span v-else-if="analysisError" class="auto-status error-text">{{ analysisError }}</span>
              <span v-else-if="analysisWarning" class="auto-status warning-text">{{ analysisWarning }}</span>
              <span v-else class="auto-status muted">Results update as you select proteins</span>
            </div>
          </div>
        </div>
      </div>

      <!-- Right Results Panel -->
      <div class="results-panel">
        <div class="results-header">
          <div class="results-title">
            <i class="el-icon-share"></i>
            <span>Drug Target Analysis</span>
          </div>

          <div v-if="hasResults" class="stats-chips">
            <span class="stat-chip">{{ geneCount }} Genes</span>
            <span class="stat-chip">{{ drugCount }} Drugs</span>
            <span class="stat-chip">{{ sankeyLinks.length }} Interactions</span>
            <span v-if="truncatedCount > 0" class="stat-chip warning">{{ truncatedCount }} truncated</span>
          </div>

          <div class="results-actions" v-if="hasResults">
            <!-- Sub-tabs for switching views -->
            <div class="sub-tabs">
              <div class="sub-tabs-left">
                <button class="sub-tab" :class="{ active: activeTab === 'network' }" @click="activeTab = 'network'">
                  <i class="el-icon-share"></i> Plot
                </button>
                <button class="sub-tab" :class="{ active: activeTab === 'table' }" @click="activeTab = 'table'">
                  <i class="el-icon-document"></i> Table
                </button>
              </div>
              <div class="sub-tabs-actions">
                <!-- Download plot - only when plot is shown -->
                <el-button v-if="activeTab === 'network'" size="mini" @click="downloadPlotPNG" plain>
                  <i class="el-icon-picture-outline"></i> Download Plot
                </el-button>
                <!-- Download table - only when table is shown -->
                <el-button v-if="activeTab === 'table'" size="mini" type="primary" @click="downloadAllTable">
                  <i class="el-icon-download"></i> Download Table
                </el-button>
              </div>
            </div>
          </div>
        </div>

        <div class="results-container">
          <template v-if="hasResults">
            <div v-show="activeTab === 'network'" class="plot-pane">
              <div class="chart-wrapper" :style="{ height: chartHeight + 'px' }">
                <v-chart ref="networkChart" class="chart" :option="sankeyOption" autoresize @click="handleChartClick" />
              </div>
            </div>

            <div v-show="activeTab === 'table'" class="table-pane">
              <div class="table-toolbar">
                <div class="table-toolbar-left">
                  <el-input
                    v-model="tableSearch"
                    size="small"
                    clearable
                    placeholder="Search gene or drug..."
                    class="table-search"
                    @input="handleTableSearchInput"
                  />
                  <span class="table-meta">
                    Total {{ tableTotal }} rows
                  </span>
                </div>

                <div class="table-toolbar-right">
                  <el-popover v-if="truncatedCount > 0" placement="bottom" width="360" trigger="hover">
                    <div class="truncate-popover">
                      <div class="truncate-title">Truncated genes (plot only)</div>
                      <div v-for="t in truncatedGenes" :key="t.gene" class="truncate-row">
                        <span class="truncate-gene">{{ t.gene }}</span>
                        <span class="truncate-info">{{ t.shown }} / {{ t.total }} shown</span>
                      </div>
                    </div>
                    <el-tag slot="reference" size="mini" type="warning" effect="plain">
                      {{ truncatedCount }} truncated
                    </el-tag>
                  </el-popover>
                </div>
              </div>

              <div class="table-wrapper">
                <el-table
                  v-loading="tableLoading"
                  :data="tableRows"
                  border
                  stripe
                  height="100%"
                >
                  <el-table-column prop="gene" label="Gene/Protein" min-width="120" show-overflow-tooltip />
                  <el-table-column prop="drug" label="Drug" min-width="160" show-overflow-tooltip />
                  <el-table-column prop="drugId" label="Drug ID" width="100" show-overflow-tooltip />
                  <el-table-column prop="type" label="Type" width="110">
                    <template slot-scope="scope">
                      <el-tag v-if="scope.row.type" :type="scope.row.type === 'biotech' ? 'success' : 'primary'" size="mini">
                        {{ scope.row.type }}
                      </el-tag>
                    </template>
                  </el-table-column>
                  <el-table-column prop="fda" label="FDA Status" width="140" show-overflow-tooltip>
                    <template slot-scope="scope">
                      <span :class="{'fda-approved': scope.row.fda && scope.row.fda.includes('approved')}">
                        {{ scope.row.fda }}
                      </span>
                    </template>
                  </el-table-column>
                  <el-table-column prop="actions" label="Actions" width="120" show-overflow-tooltip />
                  <el-table-column prop="drug_gene_count" label="# Targets" width="90" />
                  <el-table-column label="Copy" width="70">
                    <template slot-scope="scope">
                      <el-button type="text" size="mini" @click="copyText(scope.row.drug)">Drug</el-button>
                    </template>
                  </el-table-column>
                </el-table>
              </div>

              <div class="table-pagination">
                <el-pagination
                  background
                  layout="total, sizes, prev, pager, next"
                  :total="tableTotal"
                  :page-sizes="[10, 20, 50, 100]"
                  :page-size="tablePageSize"
                  :current-page="tablePage"
                  @size-change="handlePageSizeChange"
                  @current-change="handlePageChange"
                />
              </div>
            </div>
          </template>

          <div v-else class="plot-placeholder">
            <i class="el-icon-share"></i>
            <p>Protein-Drug Interaction Network</p>
            <span>Select proteins to view the network and table.</span>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios';
import VChart from "vue-echarts";
import * as echarts from "echarts/core";
import { TitleComponent, TooltipComponent, LegendComponent } from "echarts/components";
import { SankeyChart } from "echarts/charts";
import { CanvasRenderer } from "echarts/renderers";

echarts.use([TitleComponent, TooltipComponent, LegendComponent, SankeyChart, CanvasRenderer]);

export default {
  name: "DrugTargetAnalysis",
  components: { VChart },
  data() {
    return {
      selectedProteins: [],
      gene_list: [],
      allGenes: [],
      sankeyNodes: [],
      sankeyLinks: [],
      // Server-side table
      tableRows: [],
      tableTotal: 0,
      tableLoading: false,
      lastQueryGenes: [],
      lastQueryMaxDrugsPerGene: 10,
      loadingGenes: false,
      loadingAnalysis: false,
      analysisError: '',
      analysisWarning: '',
      maxDrugsPerGene: 10,
      truncatedGenes: [],
      resultStats: null,
      activeTab: 'network',
      tableSearch: '',
      tablePage: 1,
      tablePageSize: 20,
      geneColors: ['#667eea', '#764ba2', '#f093fb', '#f5576c', '#4facfe', '#00f2fe', '#43e97b', '#38f9d7', '#fa709a', '#fee140']
    };
  },
  created() {
    this._analysisReqId = 0
    this._fetchInteractionsDebounced = this.debounce(this.fetchInteractions, 450)
  },
  async mounted() {
    await this.loadAllGenes();
  },
  watch: {
    selectedProteins: {
      handler(newVal) {
        const genes = Array.isArray(newVal) ? newVal : []
        // Update the "last query" immediately so table paging/search works during fetch.
        this.lastQueryGenes = [...genes]
        this.lastQueryMaxDrugsPerGene = this.maxDrugsPerGene

        if (genes.length === 0) {
          this.clearResults()
          return
        }

        // New selection => reset table search + page
        this.tableSearch = ''
        this.tablePage = 1
        if (this._fetchInteractionsDebounced) this._fetchInteractionsDebounced()
      },
      deep: true
    },
    maxDrugsPerGene(newVal) {
      this.lastQueryMaxDrugsPerGene = newVal
      if (!this.lastQueryGenes || this.lastQueryGenes.length === 0) return
      if (this._fetchInteractionsDebounced) this._fetchInteractionsDebounced()
    }
  },
  computed: {
    hasResults() {
      return (this.sankeyNodes && this.sankeyNodes.length > 0) || (this.sankeyLinks && this.sankeyLinks.length > 0)
    },
    isTableFiltered() {
      return String(this.tableSearch || '').trim().length > 0
    },
    geneCount() {
      return this.sankeyNodes.filter(n => n.isGene).length
    },
    drugCount() {
      return this.sankeyNodes.filter(n => !n.isGene).length
    },
    truncatedCount() {
      return this.truncatedGenes.length
    },
    chartHeight() {
      const nodeCount = this.sankeyNodes.length
      const minHeight = 500
      const heightPerNode = 25
      return Math.max(minHeight, Math.min(nodeCount * heightPerNode, 2000))
    },
    sankeyOption() {
      const nodeCount = this.sankeyNodes.length
      const nodeGap = nodeCount > 100 ? 8 : nodeCount > 50 ? 12 : 20
      const fontSize = nodeCount > 100 ? 9 : nodeCount > 50 ? 10 : 12
      
      return {
        tooltip: {
          trigger: 'item',
          triggerOn: 'mousemove',
          backgroundColor: 'rgba(255, 255, 255, 0.95)',
          borderColor: '#e0e0e0',
          borderWidth: 1,
          padding: [12, 16],
          textStyle: { color: '#333', fontSize: 13 },
          formatter: (params) => {
            if (params.dataType === 'edge') {
              return `<div style="font-weight: 600; margin-bottom: 4px;">
                <span style="color: #667eea;">${params.data.source}</span>
                <span style="color: #999;"> → </span>
                <span style="color: #764ba2;">${params.data.target}</span>
              </div><div style="color: #999; font-size: 11px;">Gene-Drug interaction</div>`
            }
            const isGene = params.data.isGene
            const icon = isGene ? '🧬' : '💊'
            const connections = this.sankeyLinks.filter(l => isGene ? l.source === params.name : l.target === params.name).length
            const connectionLabel = isGene ? 'Target drugs' : 'Target genes'
            
            if (isGene) {
              return `<div style="font-weight: 600; font-size: 14px; margin-bottom: 6px;">${icon} ${params.name}</div>
                <div style="color: #666;"><div>Type: <strong>Gene/Protein</strong></div><div>${connectionLabel}: <strong>${connections}</strong></div></div>
                <div style="color: #999; font-size: 11px; margin-top: 6px; border-top: 1px solid #eee; padding-top: 6px;">📋 Click to copy name</div>`
            } else {
              // Drug node - show additional info
              const drugId = params.data.drugId || ''
              const drugType = params.data.type || ''
              const fda = params.data.fda || ''
              const actions = params.data.actions || ''
              
              let infoHtml = `<div style="font-weight: 600; font-size: 14px; margin-bottom: 6px;">${icon} ${params.name}</div>`
              infoHtml += `<div style="color: #666; line-height: 1.6;">`
              if (drugId) infoHtml += `<div>Drug ID: <strong>${drugId}</strong></div>`
              if (drugType) infoHtml += `<div>Type: <strong style="color: ${drugType === 'biotech' ? '#67c23a' : '#409eff'};">${drugType}</strong></div>`
              if (fda) infoHtml += `<div>FDA: <strong style="color: ${fda.includes('approved') ? '#67c23a' : '#909399'};">${fda}</strong></div>`
              if (actions) infoHtml += `<div>Action: <strong>${actions}</strong></div>`
              infoHtml += `<div>${connectionLabel}: <strong>${connections}</strong></div>`
              infoHtml += `</div>`
              infoHtml += `<div style="color: #999; font-size: 11px; margin-top: 6px; border-top: 1px solid #eee; padding-top: 6px;">📋 Click to copy name</div>`
              return infoHtml
            }
          }
        },
        series: [{
          type: 'sankey',
          left: 120,
          right: 200,
          top: 30,
          bottom: 30,
          layout: 'none',
          layoutIterations: 32,
          nodeWidth: 18,
          nodeGap: nodeGap,
          emphasis: { focus: 'adjacency', lineStyle: { opacity: 0.8 } },
          blur: { itemStyle: { opacity: 0.3 }, lineStyle: { opacity: 0.1 } },
          data: this.sankeyNodes,
          links: this.sankeyLinks,
          lineStyle: { color: 'gradient', curveness: 0.4, opacity: 0.4 },
          label: {
            show: true,
            position: 'right',
            fontSize: fontSize,
            fontWeight: 500,
            color: '#333',
            formatter: (params) => params.name.length > 20 ? params.name.substring(0, 18) + '...' : params.name
          },
          itemStyle: { borderRadius: 3, borderWidth: 1, borderColor: 'rgba(255,255,255,0.3)' },
          cursor: 'pointer',
          levels: [
            { depth: 0, label: { position: 'left', fontWeight: 'bold', fontSize: fontSize + 2, formatter: (params) => params.name.length > 12 ? params.name.substring(0, 10) + '...' : params.name }, itemStyle: { borderRadius: 4 } },
            { depth: 1, label: { position: 'right' } }
          ]
        }],
        dataZoom: nodeCount > 50 ? [{ type: 'inside', orient: 'vertical', filterMode: 'none' }] : []
      }
    }
  },
  methods: {
    debounce(fn, wait = 300) {
      let t = null
      return function (...args) {
        clearTimeout(t)
        t = setTimeout(() => fn.apply(this, args), wait)
      }
    },

    clearResults() {
      this.sankeyNodes = []
      this.sankeyLinks = []
      this.truncatedGenes = []
      this.resultStats = null
      this.tableRows = []
      this.tableTotal = 0
      this.tablePage = 1
      this.activeTab = 'network'
      this.analysisError = ''
      this.analysisWarning = ''
    },

    async loadAllGenes() {
      try {
        const response = await axios.post('/api/geneSearch/search_genes', { search: '', only_with_drugs: true })
        if (response.data.code === 200) {
          const list = response.data.data?.content?.search_list || []
          this.allGenes = list
          this.gene_list = list.slice(0, 100)
        }
      } catch (error) {
        console.error('Error loading all genes:', error)
      }
    },
    
    async fetchGenes(query) {
      try {
        this.loadingGenes = true
        if (!query || String(query).trim() === '') {
          this.gene_list = this.allGenes.slice(0, 100)
          return
        }
        const response = await axios.post('/api/geneSearch/search_genes', { search: query, only_with_drugs: true })
        const list = response.data?.data?.content?.search_list || []
        this.gene_list = Array.isArray(list) ? list : []
      } catch (error) {
        console.error('Error fetching gene options:', error)
        this.gene_list = []
      } finally {
        this.loadingGenes = false
      }
    },
    
    async fetchInteractions() {
      const genes = Array.isArray(this.lastQueryGenes) ? this.lastQueryGenes : []
      if (genes.length === 0) return

      const reqId = ++this._analysisReqId
      this.loadingAnalysis = true
      this.analysisError = ''
      this.analysisWarning = ''
      
      try {
        const response = await axios.post('/api/geneSearch/get_gene_drug_interactions', {
          genes: genes,
          max_drugs_per_gene: this.lastQueryMaxDrugsPerGene,
          include_plot: true,
          table_page: this.tablePage,
          table_page_size: this.tablePageSize,
          table_search: this.tableSearch || ''
        })
        
        if (response.data.code === 200) {
          // Drop stale responses (user changed inputs while request in flight)
          if (reqId !== this._analysisReqId) return

          this.sankeyNodes = response.data.data.nodes || []
          this.sankeyLinks = response.data.data.links || []
          this.truncatedGenes = response.data.data.truncated_genes || []
          this.resultStats = response.data.data.stats || null

          const table = response.data.data.table || {}
          this.tableRows = table.rows || []
          this.tableTotal = table.total || 0
          
          const missingGenes = response.data.data.missing_genes || []

          if (missingGenes.length > 0) {
            this.analysisWarning = `No drug data for: ${missingGenes.slice(0, 5).join(', ')}${missingGenes.length > 5 ? ` (+${missingGenes.length - 5} more)` : ''}`
          }
        } else {
          this.analysisError = response.data.error || 'Failed to fetch drug interactions'
        }
      } catch (error) {
        console.error('Error fetching drug interactions:', error)
        this.analysisError = 'Failed to fetch drug interactions'
      } finally {
        if (reqId === this._analysisReqId) this.loadingAnalysis = false
      }
    },

    handleGenePaste(e) {
      try {
        const clipboard = (e && e.clipboardData && e.clipboardData.getData) ? e.clipboardData.getData('text') : '';
        if (!clipboard) return;
        e.preventDefault();
        const parsed = clipboard.split(/[\n,;\t ]+/).map(s => (s || '').trim()).filter(Boolean);
        if (!parsed.length) return;
        const unique = Array.from(new Set(parsed));
        this.selectedProteins = Array.from(new Set([...(Array.isArray(this.selectedProteins) ? this.selectedProteins : []), ...unique]));
      } catch (err) {
        console.error('Failed to paste proteins', err)
      }
    },
    
    handleChartClick(params) {
      if (params.dataType !== 'node') return
      const name = params.name
      
      navigator.clipboard.writeText(name).then(() => {
      }).catch(() => {
        const textArea = document.createElement('textarea')
        textArea.value = name
        document.body.appendChild(textArea)
        textArea.select()
        document.execCommand('copy')
        document.body.removeChild(textArea)
      })
    },

    handleTableSearchInput() {
      this.tablePage = 1
      this.fetchTablePage()
    },

    handlePageSizeChange(size) {
      this.tablePageSize = size
      this.tablePage = 1
      this.fetchTablePage()
    },

    handlePageChange(page) {
      this.tablePage = page
      this.fetchTablePage()
    },

    copyText(text) {
      const value = String(text || '')
      if (!value) return
      navigator.clipboard.writeText(value).then(() => {
      }).catch(() => {
        const textArea = document.createElement('textarea')
        textArea.value = value
        document.body.appendChild(textArea)
        textArea.select()
        document.execCommand('copy')
        document.body.removeChild(textArea)
      })
    },

    downloadPlotPNG() {
      try {
        const chartRef = this.$refs.networkChart
        const chart =
          (chartRef && typeof chartRef.getDataURL === 'function' && chartRef) ||
          (chartRef && typeof chartRef.getEchartsInstance === 'function' && chartRef.getEchartsInstance()) ||
          (chartRef && chartRef.chart) ||
          null

        if (!chart || typeof chart.getDataURL !== 'function') {
          return
        }
        const url = chart.getDataURL({ type: 'png', pixelRatio: 2, backgroundColor: '#fff' })
        const link = document.createElement('a')
        link.download = `drug_target_network_${new Date().toISOString().slice(0, 10)}.png`
        link.href = url
        link.click()
      } catch (err) {
        console.error('Failed to download plot', err)
      }
    },

    downloadAllTable() {
      return this.downloadTableCSV(false)
    },

    async fetchTablePage() {
      try {
        if (!this.lastQueryGenes || this.lastQueryGenes.length === 0) return
        this.tableLoading = true
        const response = await axios.post('/api/geneSearch/get_gene_drug_interactions', {
          genes: this.lastQueryGenes,
          max_drugs_per_gene: this.lastQueryMaxDrugsPerGene,
          include_plot: false,
          table_page: this.tablePage,
          table_page_size: this.tablePageSize,
          table_search: this.tableSearch || ''
        })
        if (response.data.code === 200) {
          const table = response.data.data.table || {}
          this.tableRows = table.rows || []
          this.tableTotal = table.total || 0
        } else {
          this.analysisError = response.data.error || 'Failed to load table'
        }
      } catch (e) {
        this.analysisError = 'Failed to load table'
      } finally {
        this.tableLoading = false
      }
    },

    async downloadTableCSV(filtered = false) {
      try {
        if (!this.lastQueryGenes || this.lastQueryGenes.length === 0) return
        this.tableLoading = true
        const response = await axios.post('/api/geneSearch/get_gene_drug_interactions', {
          genes: this.lastQueryGenes,
          max_drugs_per_gene: this.lastQueryMaxDrugsPerGene,
          include_plot: false,
          export_table: true,
          table_search: filtered ? (this.tableSearch || '') : ''
        })
        if (response.data.code !== 200) {
          this.analysisError = response.data.error || 'Failed to export table'
          return
        }

        const rows = response.data.data?.table?.rows || []
        if (!rows.length) return

        const headers = ['gene', 'drug', 'drug_gene_count', 'gene_total_drugs']
        const escapeCSV = (v) => {
          const s = String(v ?? '')
          if (/[",\n\r]/.test(s)) return `"${s.replace(/"/g, '""')}"`
          return s
        }
        const csv = [
          headers.join(','),
          ...rows.map(r => headers.map(h => escapeCSV(r[h])).join(','))
        ].join('\n')

        const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' })
        const url = URL.createObjectURL(blob)
        const a = document.createElement('a')
        a.href = url
        a.download = `drug_target_interactions_${filtered ? 'filtered_' : ''}${new Date().toISOString().slice(0, 10)}.csv`
        a.click()
        URL.revokeObjectURL(url)
      } catch (e) {
        this.analysisError = 'Failed to export table'
      } finally {
        this.tableLoading = false
      }
    }
  }
};
</script>

<style scoped>
.drug-target-container {
  background: #f8f9fa;
  height: 100%;
  display: flex;
  flex-direction: column;
}

.module-layout {
  display: flex;
  flex-direction: row;
  padding: 16px;
  gap: 16px;
  overflow: hidden;
  flex: 1;
  min-height: 0;
}

/* Parameter Panel */
.parameter-panel {
  background: white;
  border-radius: 8px;
  width: 300px;
  flex-shrink: 0;
  display: flex;
  flex-direction: column;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.08);
}

.parameter-panel h4 {
  margin: 0;
  padding: 12px 16px;
  font-size: 15px;
  font-weight: 600;
  color: #1e293b;
  border-bottom: 1px solid #e2e8f0;
  display: flex;
  align-items: center;
  gap: 8px;
}

.parameter-panel h4 i {
  color: #3b82f6;
}

.parameter-container {
  flex: 1;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  padding: 12px 16px;
  gap: 12px;
}

.parameter-group {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.parameter-group label {
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-size: 14px;
  font-weight: 600;
  color: #334155;
}

.selected-info {
  font-size: 13px;
  color: #64748b;
}

.parameter-footer {
  margin-top: auto;
  padding-top: 12px;
  border-top: 1px solid #e2e8f0;
}

.auto-update-hint {
  display: flex;
  align-items: center;
  gap: 10px;
  font-size: 12px;
}

.auto-pill {
  display: inline-flex;
  align-items: center;
  padding: 2px 8px;
  border-radius: 999px;
  background: #ecfeff;
  color: #0f766e;
  border: 1px solid #99f6e4;
  font-weight: 700;
}

.auto-status {
  color: #334155;
  font-weight: 500;
}

.error-text {
  color: #b91c1c;
}

.warning-text {
  color: #92400e;
}

/* Results Panel */
.results-panel {
  flex: 1;
  background: white;
  border-radius: 8px;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.08);
}

.results-header {
  margin: 0;
  padding: 10px 12px;
  border-bottom: 1px solid #e2e8f0;
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
}

.results-title {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 15px;
  font-weight: 700;
  color: #1e293b;
}

.results-title i {
  color: #3b82f6;
}

.stats-chips {
  display: flex;
  gap: 8px;
  margin-left: auto;
  flex-wrap: wrap;
}

.results-actions {
  display: flex;
  align-items: center;
  gap: 8px;
  flex: 1;
}

/* Sub-tabs - Folder Style */
.sub-tabs {
  display: flex;
  align-items: center;
  gap: 12px;
  background: linear-gradient(135deg, #f1f5f9 0%, #e2e8f0 100%);
  padding: 6px 10px;
  border-radius: 8px;
  margin-left: auto;
}

.sub-tabs-left {
  display: flex;
  gap: 2px;
}

.sub-tabs-actions {
  display: flex;
  align-items: center;
  gap: 8px;
}

.sub-tab {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 8px 14px;
  border: none;
  background: transparent;
  color: #64748b;
  font-size: 13px;
  font-weight: 500;
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.2s ease;
}

.sub-tab:hover {
  background: rgba(255, 255, 255, 0.6);
  color: #3b82f6;
}

.sub-tab.active {
  background: #ffffff;
  color: #3b82f6;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
}

.sub-tab.active i {
  color: #3b82f6;
}

.sub-tab i {
  font-size: 13px;
}

.stat-chip {
  font-size: 12px;
  font-weight: 500;
  color: #64748b;
  background: #f1f5f9;
  padding: 4px 10px;
  border-radius: 12px;
}

.stat-chip.warning {
  background: #fef3c7;
  color: #92400e;
}

.results-container {
  flex: 1;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
}


.plot-pane {
  flex: 1;
  min-height: 0;
}

.chart-wrapper {
  width: 100%;
  min-height: 500px;
  padding: 16px;
}

.chart {
  width: 100%;
  height: 100%;
}

.table-toolbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 12px 16px;
  border-bottom: 1px solid #eef2f7;
}

.table-pane {
  display: flex;
  flex-direction: column;
  flex: 1;
  min-height: 0;
}

.table-toolbar-left {
  display: flex;
  align-items: center;
  gap: 12px;
  min-width: 0;
}

.table-search {
  width: 320px;
  max-width: 60vw;
}

.table-meta {
  font-size: 12px;
  color: #64748b;
  white-space: nowrap;
}

.table-wrapper {
  padding: 0 16px;
  flex: 1;
  min-height: 0;
}

.table-pagination {
  padding: 10px 16px 16px 16px;
  display: flex;
  justify-content: flex-end;
  border-top: 1px solid #eef2f7;
  background: #fff;
}

.truncate-popover {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.truncate-title {
  font-weight: 700;
  color: #1e293b;
  margin-bottom: 4px;
}

.truncate-row {
  display: flex;
  justify-content: space-between;
  gap: 10px;
  font-size: 12px;
}

.truncate-gene {
  font-weight: 600;
  color: #334155;
}

.truncate-info {
  color: #64748b;
  white-space: nowrap;
}

.fda-approved {
  color: #67c23a;
  font-weight: 600;
}

.muted {
  color: #94a3b8;
}

.plot-placeholder {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  color: #94a3b8;
  gap: 12px;
  padding: 48px;
}

.plot-placeholder i {
  font-size: 48px;
  color: #cbd5e1;
}

.plot-placeholder p {
  margin: 0;
  font-size: 16px;
  font-weight: 600;
  color: #64748b;
}

.plot-placeholder span {
  font-size: 14px;
  color: #94a3b8;
  text-align: center;
  max-width: 300px;
}

/* Responsive */
@media (max-width: 1024px) {
  .module-layout {
    flex-direction: column;
  }

  .parameter-panel {
    width: 100%;
  }

  .results-panel {
    min-height: 500px;
  }

  .table-search {
    width: 100%;
  }
}
</style>

